/**
 * Distributor carry requests (flag `distributor_carry`, MOT-159): a
 * distributor or wholesaler asks to carry brands it does not own and Alex
 * decides. See `/vendor/carry-requests` and `/admin/carry-requests` on the API.
 */
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

export type CarryRequestStatus = 'pending' | 'approved' | 'rejected' | 'revoked';
export type CarryEvidenceKind = 'line_card' | 'invoice' | 'warehouse_feed';

export interface CarryRequestProof {
  fileName: string;
  storagePath: string;
  contentType?: string | null;
  uploadedAt?: string | null;
  uploadedBy?: string;
  /** Short-lived signed read URL, on responses only. */
  url?: string;
}

export interface CarryRequest {
  id: string;
  vendorOrganizationId: string;
  vendorName: string;
  brandIds: string[];
  brandNames: string[];
  evidence: {
    kind: CarryEvidenceKind;
    link: string | null;
    note: string | null;
    proof: CarryRequestProof | null;
  };
  requestedBy: string;
  status: CarryRequestStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  revokedBy: string | null;
  revokedAt: string | null;
  source: 'vendor' | 'backfill';
  /** MAP authority per carried brand id (MOT-406); missing means never granted. */
  mapAuthority?: Record<string, CarryMapAuthorityGrant>;
  /** Admin list only: flag `distributor_map_authority` is on for this vendor. */
  mapAuthorityEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CarryMapAuthorityGrant {
  granted: boolean;
  grantedBy: string | null;
  grantedAt: string | null;
  revokedBy: string | null;
  revokedAt: string | null;
  note: string | null;
}

/** A warehouse brand skipped because it is not on the approved carry list (MOT-407). */
export interface DistributorCarrySkip {
  id: string;
  organizationId: string;
  brandKey: string;
  brand: string;
  lastRunRows: number;
  runCount: number;
  sampleRows: Array<{ sku: string; name: string; upc: string | null }>;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

export interface CarryEligibility {
  eligible: boolean;
  reason: 'not_vendor' | 'not_distributor' | 'not_approved' | null;
  vendorType: string | null;
  ownBrandId: string | null;
  ownBrandName: string | null;
  carriedBrandIds: string[];
}

export interface CreateCarryRequestInput {
  brandIds: string[];
  evidenceKind: CarryEvidenceKind;
  evidenceLink?: string;
  note?: string;
}

async function call<T>(path: string, init?: { method?: string; body?: unknown }): Promise<{ data: T; payload: any }> {
  const res = await makeApiCall(path, {
    method: init?.method || 'GET',
    headers: await getAuthHeaders(),
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.error?.message || payload?.message || 'Request failed');
  return { data: payload?.data as T, payload };
}

export const VENDOR_CARRY_REQUESTS_QUERY_KEY = ['vendor-carry-requests'] as const;
export const ADMIN_CARRY_REQUESTS_QUERY_KEY = ['admin-carry-requests'] as const;

export const listVendorCarryRequests = async (): Promise<{ requests: CarryRequest[]; eligibility: CarryEligibility }> => {
  const { data, payload } = await call<CarryRequest[]>('/vendor/carry-requests');
  return { requests: data || [], eligibility: payload?.eligibility };
};
export const createVendorCarryRequest = async (input: CreateCarryRequestInput): Promise<CarryRequest> =>
  (await call<CarryRequest>('/vendor/carry-requests', { method: 'POST', body: input })).data;
export const attachVendorCarryProof = async (
  id: string,
  proof: { fileName: string; storagePath: string; contentType?: string },
): Promise<CarryRequest> => (await call<CarryRequest>(`/vendor/carry-requests/${id}/proof`, { method: 'POST', body: proof })).data;

export const listAdminCarryRequests = async (status?: CarryRequestStatus): Promise<CarryRequest[]> =>
  (await call<CarryRequest[]>(`/admin/carry-requests${status ? `?status=${status}` : ''}`)).data || [];
export const approveCarryRequest = async (id: string, note?: string): Promise<CarryRequest> =>
  (await call<CarryRequest>(`/admin/carry-requests/${id}/approve`, { method: 'POST', body: { note: note || undefined } })).data;
export const rejectCarryRequest = async (id: string, reason: string): Promise<CarryRequest> =>
  (await call<CarryRequest>(`/admin/carry-requests/${id}/reject`, { method: 'POST', body: { reason } })).data;
export const revokeCarryRequest = async (id: string, reason: string): Promise<CarryRequest> =>
  (await call<CarryRequest>(`/admin/carry-requests/${id}/revoke`, { method: 'POST', body: { reason } })).data;

export const setCarryMapAuthority = async (id: string, brandId: string, granted: boolean): Promise<CarryRequest> =>
  (await call<CarryRequest>(`/admin/carry-requests/${id}/map-authority`, { method: 'POST', body: { brandId, granted } })).data;

export const ADMIN_NOT_CARRIED_QUERY_KEY = ['admin-carry-not-carried'] as const;
export const listNotCarried = async (): Promise<DistributorCarrySkip[]> =>
  (await call<DistributorCarrySkip[]>('/admin/carry-requests/not-carried')).data || [];
export const approveNotCarried = async (id: string): Promise<{ requestId: string | null; brandId: string; brandName: string }> =>
  (await call<{ requestId: string | null; brandId: string; brandName: string }>(`/admin/carry-requests/not-carried/${encodeURIComponent(id)}/approve`, { method: 'POST' })).data;

/** The carried brands this vendor may publish MAP for besides its own (MOT-406). */
export interface VendorMapAuthority {
  enabled: boolean;
  ownBrandName: string | null;
  brands: Array<{ brandId: string; brandName: string; grantedAt: string | null }>;
}
export const VENDOR_MAP_AUTHORITY_QUERY_KEY = ['vendor-map-authority'] as const;
export const getVendorMapAuthority = async (): Promise<VendorMapAuthority> =>
  (await call<VendorMapAuthority>('/vendor/map-authority')).data;

/** Storage path the vendor uploads evidence to; the API only accepts this prefix (same rules as a dealer letter). */
export const carryProofStoragePath = (organizationId: string, requestId: string, fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || 'pdf';
  return `brand-authorization-proof/${organizationId}/carry-${requestId}/${Date.now()}.${extension}`;
};
